import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Alert, Progress, Space, Typography, message } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faShield, faCheck } from '@fortawesome/free-solid-svg-icons'

import { validatePassphraseStrength, setupUserPassphrase } from '../utils/passphraseUtils'
import authReducer from '../utils/reducer'
import AuthEnums from '../utils/enums'
import { handleError } from '../../lib/utils'

const { Title, Text } = Typography

const PassphraseSetupModal = ({ visible, onCancel, onSuccess }) => {
  const dispatch = useDispatch()
  const authState = useSelector((state) => state.auth)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [showPassphrase, setShowPassphrase] = useState(false)
  const [showConfirmPassphrase, setShowConfirmPassphrase] = useState(false)
  const [passphrase, setPassphrase] = useState('')
  const [confirmPassphrase, setConfirmPassphrase] = useState('')
  const [validation, setValidation] = useState({ isValid: false, errors: [] })

  // Calculate password strength percentage
  const getStrengthPercentage = () => {
    if (!passphrase) return 0

    let score = 0
    const checks = [
      passphrase.length >= 8,
      /[A-Z]/.test(passphrase),
      /[a-z]/.test(passphrase),
      /[0-9]/.test(passphrase),
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(passphrase),
      passphrase.length >= 12
    ]

    score = checks.filter((check) => check).length
    return Math.round((score / checks.length) * 100)
  }

  const getStrengthColor = () => {
    const percentage = getStrengthPercentage()
    if (percentage < 40) return '#ff4d4f'
    if (percentage < 70) return '#faad14'
    return '#52c41a'
  }

  const getStrengthText = () => {
    const percentage = getStrengthPercentage()
    if (percentage < 40) return 'Weak'
    if (percentage < 70) return 'Medium'
    return 'Strong'
  }

  useEffect(() => {
    if (passphrase) {
      const validationResult = validatePassphraseStrength(passphrase)
      setValidation(validationResult)
    } else {
      setValidation({ isValid: false, errors: [] })
    }
  }, [passphrase])

  const handleSubmit = async () => {
    try {
      await form.validateFields()

      if (!validation.isValid) {
        message.error('Please address the passphrase requirements')
        return
      }

      if (passphrase !== confirmPassphrase) {
        message.error('Passphrases do not match')
        return
      }

      setLoading(true)

      // Use email as salt, fallback to phone number
      const salt = authState.agiliteUser.email || authState.agiliteUser.phoneNumber

      if (!salt) {
        throw new Error('Unable to create secure passphrase - missing user identifier')
      }

      const updatedUser = await setupUserPassphrase(authState.agiliteUser._id, passphrase, salt)

      // Update the user in state
      dispatch(authReducer.actions.setAgiliteUser(updatedUser))
      dispatch(authReducer.actions.setPassphraseSetupRequired(false))

      message.success('Backup passphrase set up successfully!')

      // Clear form
      form.resetFields()
      setPassphrase('')
      setConfirmPassphrase('')

      if (onSuccess) {
        onSuccess(updatedUser)
      }
    } catch (error) {
      console.error('Error setting up passphrase:', error)
      message.error(handleError(error, true))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setPassphrase('')
    setConfirmPassphrase('')
    setValidation({ isValid: false, errors: [] })

    if (onCancel) {
      onCancel()
    }
  }

  return (
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={faShield} style={{ color: '#1890ff' }} />
          <span>Set Up Backup Passphrase</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
      maskClosable={false}
      closable={false}
    >
      <div style={{ marginBottom: 20 }}>
        <Alert
          message='Backup Passphrase Required'
          description='For security purposes, you must set up a backup passphrase before accessing the portal. This ensures you can always access your account even if SMS/email verification fails.'
          type='warning'
          showIcon
          style={{ marginBottom: 16 }}
        />
      </div>

      <Form form={form} layout='vertical' onFinish={handleSubmit} autoComplete='off'>
        <Form.Item
          label='Backup Passphrase'
          name='passphrase'
          rules={[
            { required: true, message: 'Please enter a passphrase' },
            { min: 8, message: 'Passphrase must be at least 8 characters' }
          ]}
        >
          <Input.Password
            placeholder='Enter your backup passphrase'
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            iconRender={(visible) => (
              <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} onClick={() => setShowPassphrase(!showPassphrase)} />
            )}
            visibilityToggle={{
              visible: showPassphrase,
              onVisibleChange: setShowPassphrase
            }}
          />
        </Form.Item>

        {passphrase && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Passphrase Strength: {getStrengthText()}</Text>
            <Progress
              percent={getStrengthPercentage()}
              strokeColor={getStrengthColor()}
              showInfo={false}
              size='small'
              style={{ marginTop: 4 }}
            />
          </div>
        )}

        <Form.Item
          label='Confirm Passphrase'
          name='confirmPassphrase'
          rules={[
            { required: true, message: 'Please confirm your passphrase' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('passphrase') === value) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('Passphrases do not match!'))
              }
            })
          ]}
        >
          <Input.Password
            placeholder='Confirm your backup passphrase'
            value={confirmPassphrase}
            onChange={(e) => setConfirmPassphrase(e.target.value)}
            iconRender={(visible) => (
              <FontAwesomeIcon
                icon={visible ? faEyeSlash : faEye}
                onClick={() => setShowConfirmPassphrase(!showConfirmPassphrase)}
              />
            )}
            visibilityToggle={{
              visible: showConfirmPassphrase,
              onVisibleChange: setShowConfirmPassphrase
            }}
          />
        </Form.Item>

        {validation.errors.length > 0 && (
          <Alert
            message='Passphrase Requirements'
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validation.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            type='warning'
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading}
            disabled={!validation.isValid || passphrase !== confirmPassphrase}
            icon={<FontAwesomeIcon icon={faCheck} />}
            size='large'
          >
            Set Up Backup Passphrase
          </Button>
        </div>
      </Form>

      <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
        <Text type='secondary' style={{ fontSize: '12px' }}>
          <strong>Security Note:</strong> Your passphrase is encrypted and stored securely. It can be used as an
          alternative login method if SMS or email verification fails.
        </Text>
      </div>
    </Modal>
  )
}

export default PassphraseSetupModal
